import {
  FRIENDLY_NAMES_BY_DOMAIN_TYPE,
  CONNECTOR_DOMAIN_COLOR,
  COLORS_BY_CHAIN_INDEX,
  DEEMPHASIZED_DOMAIN_OPACITY,
} from 'antibodies/AntibodyConstants';
import {
  AntibodyFormat,
  AntibodyFormatDomain,
  DistinctDomainKey,
  DistinctDomainKeyWithChainIndex,
} from 'antibodies/AntibodyTypedefs';
import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {AntibodyDomainType, AntibodyChainType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {hexToRgbObject} from 'ui-library/Colors';

/**
 * Get a short display name for the associated entity field for this distinct domain.
 * Intended use is for inline labels, such as those in schema configuration or antibody viz.
 *
 * @deprecated Use distinctDomain.label instead.
 */
export function deprecatedDistinctDomainToFieldName({
  type,
  specificity,
}: Pick<DistinctDomainKey, 'type' | 'specificity'>): string {
  return `${type}${specificity ?? ''}`;
}

/**
 * Get the display label for the associated entity field for this distinct domain. If the friendly name is not
 * the same as the type, the abbreviation will be shown in parentheses. If the specificity is not null, it
 * will be shown after the friendly name, and if relevant, after the abbreviation.
 * Intended use is for displaying above an input where one can view and/or set an entity for this field.
 *
 * e.g. "CL", "Hinge (H)"
 */
export function distinctDomainToFieldLabel({type, specificity}: DistinctDomainKey): string {
  let friendlyName = FRIENDLY_NAMES_BY_DOMAIN_TYPE[type];
  let abbreviation = '';

  // Only show the abbreviation if the friendly name is not the same as the type, e.g. hinges and linkers
  if (friendlyName !== type) {
    abbreviation = type;
  }

  if (specificity != null) {
    friendlyName = `${friendlyName} ${specificity}`;
    if (abbreviation !== '') {
      abbreviation = `${abbreviation} ${specificity}`;
    }
  }
  return `${friendlyName}${abbreviation !== '' ? ` (${abbreviation})` : ''}`;
}

/**
 * Get a stringy key for this distinct domain.
 * Intended use is for managing the React render cycle; i.e. passing as a `key` prop.
 *
 * @deprecated Use distinctDomain.label instead.
 */
export function deprecatedDistinctDomainToKey({
  type,
  specificity,
}: Pick<DistinctDomainKey, 'type' | 'specificity'>): string {
  return specificity != null ? `${type}-${specificity}` : type;
}

function isConnectorDomain(domainType: AntibodyDomainType): boolean {
  return [AntibodyDomainType.H, AntibodyDomainType.J, AntibodyDomainType.L].includes(domainType);
}

export function isConstantDomain(domainType: AntibodyDomainType): boolean {
  return [
    AntibodyDomainType.CA,
    AntibodyDomainType.CB,
    AntibodyDomainType.CD,
    AntibodyDomainType.CG,
    AntibodyDomainType.CH1,
    AntibodyDomainType.CH2,
    AntibodyDomainType.CH3,
    AntibodyDomainType.CH4,
    AntibodyDomainType.CL,
  ].includes(domainType);
}

export function isVariableDomain(domainType: AntibodyDomainType): boolean {
  return [
    AntibodyDomainType.VA,
    AntibodyDomainType.VB,
    AntibodyDomainType.VD,
    AntibodyDomainType.VG,
    AntibodyDomainType.VH,
    AntibodyDomainType.VHH,
    AntibodyDomainType.VL,
    AntibodyDomainType.F,
  ].includes(domainType);
}

function blendWithWhite(colorValue: number, opacity: number): string {
  return (colorValue + (255 - colorValue) * opacity).toFixed(1);
}

/**
 * Deemphasize a color by blending with white. Better than opacity because we have overlapping domains.
 * Adapted from https://stackoverflow.com/a/12228643
 */
function deemphasizeColor(color: string, opacity: number = DEEMPHASIZED_DOMAIN_OPACITY): string {
  const {r, g, b} = hexToRgbObject(color);
  return `rgb(${blendWithWhite(r, opacity)}, ${blendWithWhite(g, opacity)}, ${blendWithWhite(b, opacity)})`;
}

export function domainColor({
  distinctDomain,
  deemphasized,
  selected,
}: {
  distinctDomain: Pick<DistinctDomainKeyWithChainIndex, 'type' | 'chainIndex'>;
  deemphasized?: boolean;
  selected?: boolean;
}): {baseColor: string; fill: string; stroke: string} {
  // Connectors don't have fill - they're just `path`s.
  if (isConnectorDomain(distinctDomain.type)) {
    // Note that there is no special color for selected connectors.
    const stroke = deemphasized ? deemphasizeColor(CONNECTOR_DOMAIN_COLOR) : CONNECTOR_DOMAIN_COLOR;
    return {baseColor: CONNECTOR_DOMAIN_COLOR, fill: 'transparent', stroke};
  } else {
    assert(
      distinctDomain.chainIndex >= 0 && distinctDomain.chainIndex < COLORS_BY_CHAIN_INDEX.length,
      `Not enough colors configured for chain index ${distinctDomain.chainIndex}`
    );
    const chainColorSet = COLORS_BY_CHAIN_INDEX[distinctDomain.chainIndex];
    let baseFill: string;
    if (isVariableDomain(distinctDomain.type)) {
      baseFill = chainColorSet.variableDomainColor;
    } else if (isConstantDomain(distinctDomain.type)) {
      baseFill = chainColorSet.constantDomainColor;
    } else {
      assert(false, `Invalid domain type: ${distinctDomain.type}`);
    }
    const fill = deemphasized ? deemphasizeColor(baseFill) : baseFill;
    const stroke = selected ? chainColorSet.chainColor : 'transparent';
    return {baseColor: baseFill, fill, stroke};
  }
}

export function chainColor(chainIndex: number, chainType: AntibodyChainType): string {
  if (chainType === AntibodyChainType.JOINING) {
    return CONNECTOR_DOMAIN_COLOR;
  }
  assert(chainIndex >= 0 && chainIndex < COLORS_BY_CHAIN_INDEX.length, `Invalid chain index: ${chainIndex}`);
  return COLORS_BY_CHAIN_INDEX[chainIndex].chainColor;
}

/**
 * Maps each antibody domain ref to its corresponding domain.
 */
export function formatToDomainsByRef(
  format: Pick<AntibodyFormat, 'domains'>
): Record<number, AntibodyFormatDomain> {
  return format.domains.reduce<Record<number, AntibodyFormatDomain>>((acc, domain) => {
    acc[domain.ref] = domain;
    return acc;
  }, {});
}

/**
 * Builds a list of all distinct (domain type, specificity) pairs in the format. Preserves the order of the
 * domains in the format.
 * @deprecated Use format.distinctDomains instead.
 */
export function deprecatedFormatToSortedDistinctDomains(format: {
  domains: Array<Pick<AntibodyFormatDomain, 'type' | 'specificity'>>;
}): Array<{type: AntibodyDomainType; specificity: string | null}> {
  const distinctDomains: Array<{type: AntibodyDomainType; specificity: string | null}> = [];
  for (const domain of format.domains) {
    // O(nlogn), yuck, but we should (TM) only be dealing with O(100) domains at a time. Python lets you use
    // a set for tuples of primitives!!!
    const alreadyFound = distinctDomains.find(
      (other) => other.type === domain.type && other.specificity === domain.specificity
    );
    if (!alreadyFound) {
      distinctDomains.push({type: domain.type, specificity: domain.specificity});
    }
  }
  return distinctDomains;
}

/**
 * Returns the list of all distinct (domain type, specificity) pairs in the format, together with index of the
 * chain that the domain belongs to (for visualization). Uses GraphQL data for distinct domains and chains.
 */
export function formatToSortedDistinctDomainsWithChainIndex(
  format: Pick<AntibodyFormat, 'distinctDomains' | 'distinctChains'>
): Array<DistinctDomainKeyWithChainIndex> {
  // Build a mapping from domain refs to chain index using the GraphQL distinct chains data
  const domainRefToChainIndex = format.distinctChains.reduce<Record<number, number>>((acc, chain, idx) => {
    for (const domainRef of chain.domains.flatMap((domain) => domain.refs)) {
      acc[domainRef] = idx;
    }
    return acc;
  }, {});

  // Use the first ref from each distinct domain to determine its chain index
  const distinctDomainsWithChainIndex: Array<DistinctDomainKeyWithChainIndex> = format.distinctDomains.map(
    (distinctDomain) => {
      const chainIndices = new Set(distinctDomain.refs.map((ref) => domainRefToChainIndex[ref]));
      assert(chainIndices.size === 1, 'all domains refs should belong to the same chain');
      const chainIndex = Array.from(chainIndices)[0];
      assert(chainIndex != null, 'expected at least one chain index for a distinct domain');
      return {
        type: distinctDomain.type,
        specificity: distinctDomain.specificity,
        label: distinctDomain.label,
        chainIndex,
      };
    }
  );

  return distinctDomainsWithChainIndex;
}

/**
 * Transforms the label for a chain to the legacy label if the flag is enabled.
 */
export function getChainLabel(label: string): string {
  return BenchlingConfig.ANTIBODY_ENABLE_LEGACY_CHAIN_LABELS && label === 'Chain' ? 'Single' : label;
}
